/**
 * Nordic Frost — cookie consent + preferences. Self-contained: injects its own
 * styles so it works on every page. GDPR-correct — necessary is always on,
 * analytics/marketing default OFF, and Accept-all / Necessary-only carry equal
 * weight. Public API: window.NordicConsent.open() / .get(); any [data-cookie-prefs]
 * element reopens the modal; a 'nordic:consent' event fires with {analytics, marketing}.
 */
interface Consent {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: string;
}

declare global {
  interface Window {
    NordicConsent: { open: () => void; get: () => Consent | null };
  }
}

const KEY = "nil-consent-v1";

function read(): Consent | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

function save(analytics: boolean, marketing: boolean): Consent {
  const data: Consent = { necessary: true, analytics: !!analytics, marketing: !!marketing, ts: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* localStorage unavailable */
  }
  document.dispatchEvent(new CustomEvent("nordic:consent", { detail: data }));
  return data;
}

/* styles — hard-coded tokens; no shell dependency */
const CSS =
  ".nc-root{position:fixed;inset:0;z-index:9999;pointer-events:none;font-family:Geist,system-ui,-apple-system,BlinkMacSystemFont,sans-serif;}" +
  ".nc-banner{pointer-events:auto;position:absolute;left:50%;bottom:22px;transform:translateX(-50%);width:min(680px,calc(100% - 32px));" +
  "background:rgba(16,17,22,.86);backdrop-filter:blur(22px) saturate(160%);-webkit-backdrop-filter:blur(22px) saturate(160%);" +
  "border:1px solid rgba(255,255,255,.1);border-radius:18px;box-shadow:0 24px 80px rgba(0,0,0,.55);padding:22px 24px;" +
  "opacity:0;translate:0 14px;transition:opacity .4s ease,translate .4s cubic-bezier(.32,.72,0,1);}" +
  ".nc-banner.in{opacity:1;translate:0 0;}" +
  '.nc-banner .nc-eyebrow{font-family:"Geist Mono",ui-monospace,Menlo,monospace;font-size:.64rem;letter-spacing:.22em;' +
  "text-transform:uppercase;color:#2fe6c4;display:flex;align-items:center;gap:8px;margin-bottom:10px;}" +
  ".nc-banner .nc-eyebrow svg{width:14px;height:14px;stroke:#2fe6c4;fill:none;}" +
  ".nc-banner p{color:#c7ccd6;font-size:.92rem;line-height:1.58;margin:0 0 18px;}" +
  ".nc-banner p a{color:#bfe2ff;text-decoration:underline;text-underline-offset:2px;}" +
  ".nc-actions{display:flex;gap:10px;flex-wrap:wrap;}" +
  ".nc-btn{font-family:inherit;font-size:.88rem;font-weight:500;cursor:pointer;border-radius:11px;padding:11px 18px;" +
  "border:1px solid transparent;transition:transform .2s ease,box-shadow .2s ease,background .2s ease,border-color .2s ease;white-space:nowrap;}" +
  ".nc-btn:focus-visible{outline:2px solid #6cb8ff;outline-offset:2px;}" +
  ".nc-accept{background:linear-gradient(135deg,#dff0ff,#bfe2ff);color:#08141f;box-shadow:0 8px 28px rgba(108,184,255,.26);}" +
  ".nc-accept:hover{transform:translateY(-2px);box-shadow:0 12px 36px rgba(108,184,255,.4);}" +
  ".nc-reject{background:rgba(255,255,255,.04);color:#f3f5f8;border-color:rgba(255,255,255,.14);}" +
  ".nc-reject:hover{transform:translateY(-2px);border-color:rgba(47,230,196,.45);background:rgba(47,230,196,.06);}" +
  ".nc-custom{background:none;color:#9097a3;padding:11px 14px;margin-left:auto;}" +
  ".nc-custom:hover{color:#f3f5f8;}" +
  ".nc-modal{pointer-events:auto;position:absolute;inset:0;display:grid;place-items:center;padding:20px;" +
  "background:rgba(8,9,12,.62);backdrop-filter:blur(6px);opacity:0;transition:opacity .3s ease;}" +
  ".nc-modal.in{opacity:1;}" +
  ".nc-card{width:min(540px,100%);max-height:88vh;overflow:auto;background:rgba(16,17,22,.94);" +
  "border:1px solid rgba(255,255,255,.1);border-radius:20px;box-shadow:0 30px 90px rgba(0,0,0,.6);padding:28px;" +
  "translate:0 12px;transition:translate .3s cubic-bezier(.32,.72,0,1);}" +
  ".nc-modal.in .nc-card{translate:0 0;}" +
  ".nc-card h2{font-size:1.3rem;font-weight:700;letter-spacing:-.02em;color:#f3f5f8;margin:0 0 8px;}" +
  ".nc-card .nc-sub{color:#9097a3;font-size:.9rem;line-height:1.55;margin:0 0 22px;}" +
  ".nc-opt{display:flex;gap:14px;align-items:flex-start;padding:16px 0;border-top:1px solid rgba(255,255,255,.07);}" +
  ".nc-opt .nc-txt{flex:1;}" +
  ".nc-opt b{display:block;color:#f3f5f8;font-size:.96rem;font-weight:600;margin-bottom:4px;}" +
  ".nc-opt span{color:#9097a3;font-size:.85rem;line-height:1.5;}" +
  ".nc-sw{position:relative;width:44px;height:26px;flex:none;margin-top:2px;}" +
  ".nc-sw input{position:absolute;opacity:0;width:100%;height:100%;margin:0;cursor:pointer;}" +
  ".nc-sw .track{position:absolute;inset:0;border-radius:999px;background:rgba(255,255,255,.12);" +
  "border:1px solid rgba(255,255,255,.16);transition:background .2s,border-color .2s;}" +
  ".nc-sw .knob{position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:50%;background:#9097a3;transition:transform .2s,background .2s;}" +
  ".nc-sw input:checked+.track{background:rgba(47,230,196,.22);border-color:rgba(47,230,196,.5);}" +
  ".nc-sw input:checked+.track+.knob{transform:translateX(18px);background:#2fe6c4;}" +
  ".nc-sw input:disabled{cursor:not-allowed;}" +
  ".nc-sw input:disabled+.track{background:rgba(47,230,196,.16);border-color:rgba(47,230,196,.32);}" +
  ".nc-sw input:disabled+.track+.knob{transform:translateX(18px);background:#6cb8ff;}" +
  ".nc-sw input:focus-visible+.track{outline:2px solid #6cb8ff;outline-offset:2px;}" +
  ".nc-modal-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:24px;}" +
  ".nc-modal-actions .nc-btn{flex:1;min-width:140px;text-align:center;}" +
  "@media (max-width:520px){.nc-actions{flex-direction:column;align-items:stretch;}.nc-custom{margin-left:0;order:-1;}.nc-btn{text-align:center;}}" +
  "@media (prefers-reduced-motion:reduce){.nc-banner,.nc-modal,.nc-card{transition:none;translate:none;}}";

function el(html: string): HTMLElement {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstChild as HTMLElement;
}
function injectStyles(): void {
  if (document.getElementById("nc-styles")) return;
  const s = document.createElement("style");
  s.id = "nc-styles";
  s.textContent = CSS;
  document.head.appendChild(s);
}
const actOf = (e: Event): string | null => (e.target instanceof Element ? e.target.getAttribute("data-act") : null);

let root: HTMLElement | null = null;
let banner: HTMLElement | null = null;
let modal: HTMLElement | null = null;

function ensureRoot(): HTMLElement {
  if (root) return root;
  root = el('<div class="nc-root" aria-live="polite"></div>');
  document.body.appendChild(root);
  return root;
}

function dismissBanner(): void {
  if (!banner) return;
  banner.classList.remove("in");
  const b = banner;
  banner = null;
  setTimeout(() => b.parentNode?.removeChild(b), 420);
}

function showBanner(): void {
  const r = ensureRoot();
  banner = el(
    '<section class="nc-banner" role="dialog" aria-label="Cookie consent" aria-describedby="nc-desc">' +
      '<div class="nc-eyebrow"><svg viewBox="0 0 24 24" stroke-width="1.7"><path d="M12 3a9 9 0 1 0 9 9 4 4 0 0 1-5-5 4 4 0 0 1-4-4Z"/><circle cx="9.5" cy="13" r="1"/><circle cx="14" cy="15.5" r="1"/><circle cx="13.5" cy="9.5" r="1"/></svg>Your privacy, your choice</div>' +
      '<p id="nc-desc">We use strictly necessary cookies to run this site. With your consent we’d also use analytics to understand what’s useful. Nothing leaves the EU, and you can change this anytime. See our <a href="/legal#privacy">Privacy Policy</a>.</p>' +
      '<div class="nc-actions">' +
      '<button class="nc-btn nc-accept" data-act="all">Accept all</button>' +
      '<button class="nc-btn nc-reject" data-act="necessary">Necessary only</button>' +
      '<button class="nc-btn nc-custom" data-act="custom">Customize</button>' +
      "</div>" +
      "</section>",
  );
  r.appendChild(banner);
  requestAnimationFrame(() => banner?.classList.add("in"));
  banner.addEventListener("click", (e) => {
    const act = actOf(e);
    if (act === "all") {
      save(true, true);
      dismissBanner();
    } else if (act === "necessary") {
      save(false, false);
      dismissBanner();
    } else if (act === "custom") {
      openModal();
    }
  });
}

function opt(id: string, title: string, desc: string, checked: boolean, locked: boolean): string {
  return (
    `<label class="nc-opt" for="nc-${id}">` +
    `<span class="nc-txt"><b>${title}</b><span>${desc}</span></span>` +
    `<span class="nc-sw"><input type="checkbox" id="nc-${id}"${checked ? " checked" : ""}${locked ? " disabled checked" : ""}>` +
    '<span class="track"></span><span class="knob"></span></span>' +
    "</label>"
  );
}

let lastFocus: Element | null = null;

function escClose(e: KeyboardEvent): void {
  if (e.key === "Escape") closeModal();
}
function closeModal(): void {
  if (!modal) return;
  document.removeEventListener("keydown", escClose);
  modal.classList.remove("in");
  const m = modal;
  modal = null;
  setTimeout(() => m.parentNode?.removeChild(m), 320);
  if (lastFocus instanceof HTMLElement) lastFocus.focus();
}

function openModal(): void {
  const r = ensureRoot();
  const current = read() || { analytics: false, marketing: false };
  lastFocus = document.activeElement;
  modal = el(
    '<div class="nc-modal" role="dialog" aria-modal="true" aria-labelledby="nc-title">' +
      '<div class="nc-card">' +
      '<h2 id="nc-title">Cookie preferences</h2>' +
      '<p class="nc-sub">Choose what you’re comfortable with. We never sell your data, and analytics is processed inside the EU.</p>' +
      opt("necessary", "Strictly necessary", "Required for security, your session, and remembering this choice. Always on.", true, true) +
      opt("analytics", "Analytics", "Anonymous, EU-hosted usage metrics that tell us which pages help and which don’t.", current.analytics, false) +
      opt("marketing", "Marketing", "Used to measure campaigns and show relevant content. Off by default.", current.marketing, false) +
      '<div class="nc-modal-actions">' +
      '<button class="nc-btn nc-reject" data-act="save">Save choices</button>' +
      '<button class="nc-btn nc-accept" data-act="all">Accept all</button>' +
      "</div>" +
      "</div>" +
      "</div>",
  );
  r.appendChild(modal);
  const m = modal;
  requestAnimationFrame(() => m.classList.add("in"));

  const card = m.querySelector(".nc-card") as HTMLElement;
  m.addEventListener("click", (e) => {
    if (e.target === m) closeModal();
    const act = actOf(e);
    if (act === "save") {
      const analytics = (m.querySelector("#nc-analytics") as HTMLInputElement).checked;
      const marketing = (m.querySelector("#nc-marketing") as HTMLInputElement).checked;
      save(analytics, marketing);
      closeModal();
      dismissBanner();
    } else if (act === "all") {
      save(true, true);
      closeModal();
      dismissBanner();
    }
  });
  document.addEventListener("keydown", escClose);
  card.querySelector<HTMLElement>("button, input:not([disabled])")?.focus();
}

/* public API + boot */
window.NordicConsent = { open: openModal, get: read };

function boot(): void {
  injectStyles();
  for (const n of document.querySelectorAll("[data-cookie-prefs]")) {
    n.addEventListener("click", (e) => {
      e.preventDefault();
      openModal();
    });
  }
  const current = read();
  if (!current) showBanner();
  else save(current.analytics, current.marketing); // re-fire the event for this page load
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
